import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { useApiClient } from '@/hooks/useApiClient';
import { useMLMachines, useMLProcesses } from '@/features/machine-learning/hooks/useMLMachines';
import { useMLTools } from '@/features/machine-learning/hooks/useMLTools';
import { useMLInferenceJob } from '@/features/machine-learning/hooks/useMLInferenceJob';
import { useMLCuttingParams } from '@/features/machine-learning/hooks/useMLCuttingParams';
import { useMLSignal } from '@/features/machine-learning/hooks/useMLSignal';
import { useSignalMachines } from '@/features/signals/hooks/useSignalMachines';
import type { SignalSource, SignalsQuery } from '@/features/signals/type/SignalsTypes';
import { MLContextPanel } from '@/features/machine-learning/components/MLContextPanel';
import { MLRulHeroCard } from '@/features/machine-learning/components/MLRulHeroCard';
import { MLSignalCard } from '@/features/machine-learning/components/MLSignalCard';
import { RecentPredictionsCard } from '@/features/machine-learning/components/RecentPredictionsCard';
import { ModelPredictionApiClient } from '@/features/machine-learning/api/ModelPredictionApiClient';
import type { FeedbackRating, RecentPrediction } from '@/features/machine-learning/type/ModelPredictionTypes';

/**
 * Signals-based live RUL flow (XGBoost worker + InfluxDB signals: the last 50 passes of the
 * machine's newest part) — the pre-1.6.0 MLRulPage content, one of the two /ml views.
 * Recent runs are server-persisted (ml_predictions, source: 'signals') since
 * Phase A of the ML platform plan; the signal chart accompanies fresh runs only.
 */
export function MLSignalRulView() {
  const { t } = useTranslation();
  const http = useApiClient();
  const client = useMemo(() => (http ? new ModelPredictionApiClient(http) : null), [http]);

  const [selectedMachineId, setSelectedMachineId] = useState<number | null>(null);
  const [selectedProcessId, setSelectedProcessId] = useState<number | null>(null);
  const [selectedToolId, setSelectedToolId] = useState<string | null>(null);
  const [recentRuns, setRecentRuns] = useState<RecentPrediction[]>([]);
  // The machine the last run read, so the post-run chart shows what the model saw.
  const [runMachineId, setRunMachineId] = useState<number | null>(null);

  const machines = useMLMachines();
  // The machines with the state of their signal source (active, history) for step 2.
  const signalMachines = useSignalMachines();
  const processes = useMLProcesses(selectedMachineId);
  const { status, jobId, rulSeconds, warning, signalsWindow, error, run, cancel } =
    useMLInferenceJob();

  const selectedProcess = processes.data?.find((p) => p.id === selectedProcessId) ?? null;
  const tools = useMLTools(selectedProcess);
  const {
    params: cuttingParams,
    setParam: setCuttingParam,
    resetToDefaults: resetCuttingParams,
    isFromProcess: isParamsFromProcess,
    isUserEdited: isParamsUserEdited,
  } = useMLCuttingParams(selectedProcess);
  const selectedMachineObj = machines.data?.find((m) => m.id === selectedMachineId) ?? null;

  // The machine's signal source (its measurement in the signals database), set in Workshop →
  // Machines. The orchestrator resolves it again from the machine id; Run is disabled without it.
  const resolvedKafkaName = selectedMachineObj?.kafkaName || null;
  const listedSource =
    signalMachines.data?.machines.find((machine) => machine.id === selectedMachineId)?.source ?? null;
  const sourceIsListed = resolvedKafkaName !== null && listedSource?.name === resolvedKafkaName;
  const signalSource: SignalSource | null = resolvedKafkaName
    ? sourceIsListed
      ? listedSource
      : { name: resolvedKafkaName, influx: false, liveData: false, lastTime: null }
    : null;
  const signalSourceStatusKnown = Boolean(sourceIsListed && signalMachines.data?.influxAvailable);

  // Fires when a run completes: the last passes of the same machine's newest part, the four
  // signals the model reads, so the inference-signal card shows what the model saw.
  const signalQuery = useMemo<SignalsQuery | null>(() => {
    if (status !== 'completed' || runMachineId === null) return null;
    return { target: { machineId: runMachineId }, maxPoints: 1000 };
  }, [status, runMachineId]);

  const signal = useMLSignal(signalQuery);

  const refreshRecentRuns = useCallback(() => {
    if (!client) return;
    void client.recentPredictions('signals').then((res) => {
      if (res.ok) setRecentRuns(res.data.predictions);
    });
  }, [client]);

  useEffect(() => {
    refreshRecentRuns();
  }, [refreshRecentRuns]);

  useEffect(() => {
    if (status === 'completed') refreshRecentRuns();
  }, [status, refreshRecentRuns]);

  const handleMachineChange = useCallback((id: number) => {
    setSelectedMachineId(id);
    setSelectedProcessId(null);
    setSelectedToolId(null);
  }, []);

  const handleProcessChange = useCallback((id: number) => {
    setSelectedProcessId(id);
    setSelectedToolId(null);
  }, []);

  const handleToolChange = useCallback((toolId: string) => {
    setSelectedToolId(toolId);
  }, []);

  const handleReset = useCallback(() => {
    setSelectedMachineId(null);
    setSelectedProcessId(null);
    setSelectedToolId(null);
  }, []);

  const handleRun = useCallback(() => {
    if (
      selectedMachineId === null ||
      selectedProcessId === null ||
      selectedToolId === null ||
      resolvedKafkaName === null
    )
      return;
    const machineName =
      machines.data?.find((m) => m.id === selectedMachineId)?.name ?? String(selectedMachineId);
    const processName =
      processes.data?.find((p) => p.id === selectedProcessId)?.name ?? String(selectedProcessId);
    setRunMachineId(selectedMachineId);
    run({
      machineId: selectedMachineId,
      toolId: selectedToolId,
      ...cuttingParams,
      machine: machineName,
      processId: selectedProcessId,
      processName,
    });
  }, [
    machines.data,
    processes.data,
    selectedMachineId,
    selectedProcessId,
    selectedToolId,
    cuttingParams,
    resolvedKafkaName,
    run,
  ]);

  const handleFeedback = useCallback(
    async (predictionId: string, rating: FeedbackRating) => {
      if (!client) return;
      const res = await client.submitFeedback(predictionId, rating);
      if (res.ok) refreshRecentRuns();
    },
    [client, refreshRecentRuns]
  );

  const heroProps = {
    status,
    jobId,
    toolId: selectedToolId,
    machineId: selectedMachineId,
    machineName: machines.data?.find((m) => m.id === selectedMachineId)?.name ?? null,
    processId: selectedProcessId,
    processName: processes.data?.find((p) => p.id === selectedProcessId)?.name ?? null,
    rulSeconds,
    warning,
    signalsWindow,
    errorMessage: error,
  };

  return (
      <div className="flex flex-col gap-6">

        <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
          <div className="w-full lg:w-[360px] lg:shrink-0 lg:sticky lg:top-6">
            <MLContextPanel
              machines={machines.data ?? []}
              processes={processes.data ?? []}
              tools={tools.data ?? []}
              machinesLoading={machines.isLoading}
              processesLoading={processes.isLoading}
              toolsLoading={tools.isLoading}
              selectedMachineId={selectedMachineId}
              selectedProcessId={selectedProcessId}
              selectedToolId={selectedToolId}
              onMachineChange={handleMachineChange}
              onProcessChange={handleProcessChange}
              onToolChange={handleToolChange}
              onReset={handleReset}
              inferenceStatus={status}
              onRun={handleRun}
              onCancel={cancel}
              cuttingParams={cuttingParams}
              onCuttingParamChange={setCuttingParam}
              onCuttingParamsReset={resetCuttingParams}
              isParamsFromProcess={isParamsFromProcess}
              isParamsUserEdited={isParamsUserEdited}
              signalSource={signalSource}
              signalSourceStatusKnown={signalSourceStatusKnown}
            />
          </div>

          <div className="min-w-0 flex-1 flex flex-col gap-4">
            <MLRulHeroCard {...heroProps} />
            <MLSignalCard
              signal={signal.data ?? null}
              isLoading={signal.isLoading}
              error={signal.error}
              onRetry={signal.retry}
            />
          </div>
        </div>

        <RecentPredictionsCard
          predictions={recentRuns}
          title={t('machineLearning.modelPrediction.recent.runsTitle')}
          onFeedback={handleFeedback}
        />
      </div>
  );
}
